import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
    Send, Mail, Layers, Rss, Plus, ArrowRight, MailOpen, MousePointerClick, MailX, UserX,
} from "lucide-react";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import CreditUsageWidget from "@/components/CreditUsageWidget";
import { SandboxBanner, StatusBadge } from "@/components/outreach/shared";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

function KPI({ label, value, icon: Icon, tone = "default", testid }) {
    const toneCls =
        tone === "success" ? "text-[var(--color-success)]"
        : tone === "alert" ? "text-[var(--color-alert)]"
        : "text-[var(--color-secondary)]";
    return (
        <div
            data-testid={testid}
            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4"
        >
            <div className="flex items-center justify-between">
                <span className={`h-8 w-8 rounded-lg bg-[var(--color-panel-ai)] grid place-items-center border border-[var(--color-border)]`}>
                    <Icon className={`h-4 w-4 ${toneCls}`} />
                </span>
                <span className="text-[10px] uppercase tracking-widest text-text-secondary">
                    {label}
                </span>
            </div>
            <p className="mt-4 text-2xl font-semibold tabular-nums tracking-tight">
                {value}
            </p>
        </div>
    );
}

export default function Outreach() {
    const { user } = useAuth();
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [meta, setMeta] = useState(null);
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [m, c] = await Promise.all([
                    api.outreachMeta(),
                    api.outreachCampaigns(20, 0),
                ]);
                setMeta(m);
                setCampaigns(c.items || []);
            } catch (e) {
                toast.error(e.message || "Unable to load Outreach dashboard");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (!user) return null;
    const kpi = meta?.kpi_30d || { sent: 0, delivered: 0, opened: 0, clicked: 0, bounced: 0, unsubscribed: 0 };
    const openRate = kpi.sent ? ((kpi.opened / kpi.sent) * 100).toFixed(1) : "0.0";
    const clickRate = kpi.sent ? ((kpi.clicked / kpi.sent) * 100).toFixed(1) : "0.0";
    const bounceRate = kpi.sent ? ((kpi.bounced / kpi.sent) * 100).toFixed(1) : "0.0";
    const unsubRate = kpi.sent ? ((kpi.unsubscribed / kpi.sent) * 100).toFixed(1) : "0.0";

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex">
            <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(c => !c)} />
            <div className="flex-1 min-w-0 flex flex-col">
                <Header />
                <main data-testid="outreach-main" className="flex-1 px-4 md:px-6 py-6 max-w-6xl w-full">
                    <SandboxBanner show={meta?.sandbox} />

                    <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
                        <div>
                            <p className="text-[11px] uppercase tracking-widest text-text-secondary">
                                Outreach
                            </p>
                            <h1 className="mt-1 text-2xl md:text-3xl font-semibold tracking-tight">
                                Trasforma i tuoi lead in{" "}
                                <span className="gradient-text">conversazioni</span>
                            </h1>
                            <p className="mt-1 text-sm text-text-secondary max-w-2xl">
                                Sequenze multi-step, tracking pixel, unsubscribe compliance-first.
                                {meta?.sandbox && " — attualmente in sandbox mode."}
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-2 items-center">
                            <CreditUsageWidget />
                            <Link
                                to="/outreach/campaigns/new"
                                data-testid="outreach-new-campaign-btn"
                                className="inline-flex items-center gap-2 rounded-full gradient-bg-cta px-5 py-2.5 text-sm font-medium text-white transition-transform duration-150 hover:scale-[0.98]"
                            >
                                <Plus className="h-4 w-4" />
                                New campaign
                            </Link>
                        </div>
                    </div>

                    {/* KPI grid */}
                    {meta?.sandbox && (
                        <div
                            data-testid="kpi-sandbox-notice"
                            className="mb-2 inline-flex items-center gap-2 rounded-full border border-[var(--color-secondary)]/40 bg-[var(--color-secondary)]/10 px-3 py-1 text-[11px] text-[var(--color-secondary)]"
                        >
                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--color-secondary)] animate-pulse-soft" />
                            Simulated · sandbox KPI
                        </div>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
                        <KPI label="Sent 30d" value={kpi.sent} icon={Send} testid="kpi-sent" />
                        <KPI label="Open rate" value={`${openRate}%`} icon={MailOpen} testid="kpi-open" />
                        <KPI label="Click rate" value={`${clickRate}%`} icon={MousePointerClick} tone="success" testid="kpi-click" />
                        <KPI label="Bounce rate" value={`${bounceRate}%`} icon={MailX} tone="alert" testid="kpi-bounce" />
                        <KPI label="Unsub rate" value={`${unsubRate}%`} icon={UserX} tone="alert" testid="kpi-unsub" />
                        <KPI label="Delivered" value={kpi.delivered} icon={Mail} testid="kpi-delivered" />
                    </div>

                    {/* Shortcuts */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
                        {[
                            { to: "/outreach/templates", label: "Templates", icon: Mail, desc: "Riutilizzabili · con AI compose" },
                            { to: "/outreach/sequences", label: "Sequences", icon: Layers, desc: "Fino a 5 step · delay configurabile" },
                            { to: "/outreach/campaigns", label: "Campaigns", icon: Rss, desc: "Lancio, pausa, stats live" },
                        ].map((item, i) => (
                            <motion.div
                                key={item.to}
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.2, delay: i * 0.04 }}
                            >
                                <Link
                                    to={item.to}
                                    data-testid={`outreach-shortcut-${item.label.toLowerCase()}`}
                                    className="block rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5 hover:border-[var(--color-primary)]/60 transition-colors"
                                >
                                    <div className="flex items-center gap-2">
                                        <span className="h-8 w-8 rounded-lg bg-[var(--color-panel-ai)] grid place-items-center border border-[var(--color-border)]">
                                            <item.icon className="h-4 w-4 text-[var(--color-secondary)]" />
                                        </span>
                                        <span className="text-sm font-semibold">{item.label}</span>
                                        <ArrowRight className="h-4 w-4 text-text-secondary ml-auto" />
                                    </div>
                                    <p className="mt-3 text-xs text-text-secondary">{item.desc}</p>
                                </Link>
                            </motion.div>
                        ))}
                    </div>

                    {/* Recent campaigns */}
                    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden">
                        <div className="px-5 py-3 border-b border-[var(--color-border)] flex items-center justify-between">
                            <span className="text-sm font-medium">Recent campaigns</span>
                            <Link to="/outreach/campaigns" className="text-xs text-text-secondary hover:text-text-main">
                                See all →
                            </Link>
                        </div>
                        {loading ? (
                            <div className="p-6 text-sm text-text-secondary">Loading…</div>
                        ) : campaigns.length === 0 ? (
                            <div data-testid="outreach-no-campaigns" className="p-12 text-center">
                                <p className="text-sm text-text-secondary">
                                    Nessuna campagna ancora. Crea la prima con "New campaign".
                                </p>
                            </div>
                        ) : (
                            <ul>
                                {campaigns.slice(0, 5).map((c, i) => (
                                    <li key={c.id} className="border-b border-[var(--color-border)] last:border-b-0">
                                        <Link
                                            to={`/outreach/campaigns/${c.id}`}
                                            className="flex items-center justify-between px-5 py-3 hover:bg-[var(--color-panel-ai)]/50 transition-colors"
                                            data-testid={`campaign-shortcut-${i}`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <StatusBadge status={c.status} />
                                                <span className="text-sm font-medium">{c.name}</span>
                                            </div>
                                            <div className="flex items-center gap-4 text-xs text-text-secondary tabular-nums">
                                                <span>{c.stats?.sent || 0} sent</span>
                                                <span>{c.stats?.opened || 0} opens</span>
                                                <span>{c.stats?.clicked || 0} clicks</span>
                                                <ArrowRight className="h-3.5 w-3.5" />
                                            </div>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
}
